/* BVO Maison — theme scripts (built new) */
(function () {
  'use strict';

  /* Shopify serves every rendition off one URL, so a size is a query-string
     rewrite: 512 to measure, 1100 to display, 160 for a thumb. */
  function atWidth(url, w) {
    if (!url) return '';
    if (url.indexOf('width=') > -1) return url.replace(/width=\d+/, 'width=' + w);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
  function fileOf(url) {
    return String(url || '').split('?')[0].split('/').pop();
  }

  /* ---------- optical centring ----------
     Luxottica frames each shot to its own taste, so a colourway's five views do
     not share a centre: on RB3025 the back view sits 41.8% across the canvas
     with 4.0% of ground to its left and 20.4% to its right, and the closed
     front rides high at 32.5%. Dropped into a fixed tile the frame appears to
     wander as the shopper steps through the views.

     So the artwork is measured rather than trusted: the shot is drawn to an
     offscreen canvas at 512px and scanned on a 2px stride for pixels that are
     neither transparent nor the #F2F2F2 backdrop baked into the photography,
     which gives the bounding box of the frame itself. The gap between that
     box's centre and the middle of the tile becomes a translation, and because
     the tile carries --shot — the same grey — the ground that shifts out of
     view is indistinguishable from the ground that replaces it.

     The box is a fraction of the canvas, so one measurement serves every
     rendition of that asset: the 700 and 1100 on the page and the 1920 in the
     viewer all key to the same URL, and switching colourway or reopening the
     viewer re-uses it. Reading pixels back can fail — a CDN that drops its
     CORS header taints the canvas — so every path out of here ends at "no
     transform" rather than a shot that fails to display. */
  var optical = (function () {
    var PROBE_W = 512;      /* enough for ±0.4% on the box, small enough to be free */
    var STRIDE = 2;
    var GROUND = 234;       /* the backdrop is #F2F2F2; JPEG ringing undershoots it */
    var ALPHA = 24;
    var MAX_SHIFT = 0.25;   /* a nonsense read can never throw the shot out of frame */

    var boxes = {};         /* asset URL, query stripped -> box | null */
    var pending = {};
    var watched = [];
    var resizeTimer = 0;

    function keyOf(url) { return String(url || '').split('?')[0]; }

    /* first and last line carrying real content. A single sample is noise — a
       stray dark pixel in the JPEG — but two on one line is the temple tip. */
    function span(counts, len) {
      var lo = -1, hi = -1;
      for (var i = 0; i < len; i++) {
        if (counts[i] < 2) continue;
        if (lo === -1) lo = i;
        hi = i;
      }
      return lo === -1 ? null : [lo, hi];
    }

    function scan(img) {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return null;
      var w = Math.max(8, Math.min(PROBE_W, nw));
      var h = Math.max(8, Math.round(w * nh / nw));
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;   /* throws on a tainted canvas */

      var cols = new Uint16Array(w);
      var rows = new Uint16Array(h);
      for (var y = 0; y < h; y += STRIDE) {
        var line = y * w * 4;
        for (var x = 0; x < w; x += STRIDE) {
          var p = line + x * 4;
          if (data[p + 3] < ALPHA) continue;
          if (data[p] >= GROUND && data[p + 1] >= GROUND && data[p + 2] >= GROUND) continue;
          cols[x]++;
          rows[y]++;
        }
      }

      var hx = span(cols, w);
      var vy = span(rows, h);
      if (!hx || !vy) return null;                    /* an empty canvas: leave it alone */
      return {
        x0: hx[0] / w, x1: (hx[1] + STRIDE) / w,
        y0: vy[0] / h, y1: (vy[1] + STRIDE) / h
      };
    }

    /* the probe is its own request: asking for CORS on the displayed image
       could cost the shopper the image itself if the header ever went away */
    function measure(url) {
      var key = keyOf(url);
      if (!key) return Promise.resolve(null);
      if (Object.prototype.hasOwnProperty.call(boxes, key)) return Promise.resolve(boxes[key]);
      if (pending[key]) return pending[key];

      var job = new Promise(function (resolve) {
        function done(box) {
          boxes[key] = box;
          delete pending[key];
          resolve(box);
        }
        try {
          var probe = new Image();
          probe.crossOrigin = 'anonymous';
          probe.decoding = 'async';
          probe.onload = function () {
            var box = null;
            try { box = scan(probe); } catch (e) { box = null; }
            done(box);
          };
          probe.onerror = function () { done(null); };
          probe.src = atWidth(url, PROBE_W);
        } catch (e) { done(null); }
      });
      pending[key] = job;
      return job;
    }

    /* Both surfaces letterbox with object-fit: contain — the page tile because
       the image fills a fixed box, the viewer because the image is capped by
       the stage — so the drawn content is derived the same way for each. */
    function offsetFor(el, box) {
      if (!el || !box) return null;
      var bw = el.offsetWidth, bh = el.offsetHeight;
      var nw = el.naturalWidth, nh = el.naturalHeight;
      if (!bw || !bh || !nw || !nh) return null;

      var cw = bw, ch = bw * nh / nw;
      if (ch > bh) { ch = bh; cw = bh * nw / nh; }

      var dx = (0.5 - (box.x0 + box.x1) / 2) * cw;
      var dy = (0.5 - (box.y0 + box.y1) / 2) * ch;
      var capX = cw * MAX_SHIFT, capY = ch * MAX_SHIFT;
      if (dx > capX) dx = capX; else if (dx < -capX) dx = -capX;
      if (dy > capY) dy = capY; else if (dy < -capY) dy = -capY;
      return { x: dx, y: dy };
    }

    function centre(el, onOffset) {
      if (!el || !onOffset) return;
      var url = el.currentSrc || el.src;
      if (!url) return;
      measure(url).then(function (box) {
        /* the shopper may have changed colour while that was in flight */
        if ((el.currentSrc || el.src) !== url) return;
        /* a closed viewer has no box to centre against, and answering 0 there
           would drop the correction the next open expects to still be on */
        if (!el.offsetWidth || !el.offsetHeight) return;
        var off = box ? offsetFor(el, box) : null;
        onOffset(off ? off.x : 0, off ? off.y : 0);
      })['catch'](function () { onOffset(0, 0); });
    }

    /* one hook per image: the thumb row and the variant picker both work by
       assigning src, so every later shot arrives through the same load event */
    function watch(el, onOffset) {
      if (!el || !onOffset) return;
      var run = function () { centre(el, onOffset); };
      el.addEventListener('load', run);
      watched.push(run);
      if (el.complete && el.naturalWidth) run();
    }

    /* a resize changes the tile, not the artwork: re-run straight off the cache */
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        watched.forEach(function (run) { run(); });
      }, 150);
    });

    return { measure: measure, offsetFor: offsetFor, centre: centre, watch: watch };
  })();

  window.BVO = window.BVO || {};
  window.BVO.optical = optical;
  window.BVO.atWidth = atWidth;

  document.addEventListener('DOMContentLoaded', function () {
    /* ---------- mobile nav ---------- */
    var burger = document.querySelector('[data-menu-toggle]');
    if (burger) {
      burger.addEventListener('click', function () {
        document.body.classList.toggle('nav-open');
        burger.setAttribute('aria-expanded', document.body.classList.contains('nav-open') ? 'true' : 'false');
      });
    }

    /* ---------- product gallery ----------
       delegated: the thumb row is rebuilt whenever the colourway changes. */
    document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
      gallery.addEventListener('click', function (event) {
        var thumb = event.target.closest ? event.target.closest('[data-thumb]') : null;
        if (!thumb || !gallery.contains(thumb)) return;
        var main = gallery.querySelector('[data-main-image]');
        if (!main) return;
        main.src = thumb.getAttribute('data-src');
        if (thumb.getAttribute('data-srcset')) main.srcset = thumb.getAttribute('data-srcset');
        if (thumb.getAttribute('data-alt')) main.alt = thumb.getAttribute('data-alt');
        gallery.querySelectorAll('[data-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });

      /* the shot is centred on the frame rather than on the canvas it was
         delivered in — see the optical centring note at the top of the file */
      var mainShot = gallery.querySelector('[data-main-image]');
      if (mainShot) {
        optical.watch(mainShot, function (x, y) {
          mainShot.style.transform = (x || y)
            ? 'translate(' + x.toFixed(1) + 'px, ' + y.toFixed(1) + 'px)'
            : '';
        });
      }
    });

    /* ---------- quantity steppers ---------- */
    document.querySelectorAll('[data-qty]').forEach(function (qty) {
      var input = qty.querySelector('input');
      var plus = qty.querySelector('[data-qty-plus]');
      var minus = qty.querySelector('[data-qty-minus]');
      if (plus) plus.addEventListener('click', function () { input.value = parseInt(input.value || '1', 10) + 1; input.dispatchEvent(new Event('change', { bubbles: true })); });
      if (minus) minus.addEventListener('click', function () { var v = parseInt(input.value || '1', 10); if (v > 1) { input.value = v - 1; input.dispatchEvent(new Event('change', { bubbles: true })); } });
    });

    /* ---------- variant picker ----------
       Grouped products: colour swatches swap the gallery, price, SKU, availability
       and the ?variant= parameter in place. Sizes (and any other option) are pills. */
    document.querySelectorAll('[data-product-form]').forEach(function (wrap) {
      var jsonTag = wrap.querySelector('[data-product-json]');
      if (!jsonTag) return;
      var product;
      try { product = JSON.parse(jsonTag.textContent); } catch (e) { return; }
      if (!product || !product.variants || !product.variants.length) return;

      var colourMedia = {};
      var mediaTag = wrap.querySelector('[data-colour-media]');
      if (mediaTag) { try { colourMedia = JSON.parse(mediaTag.textContent) || {}; } catch (e) { colourMedia = {}; } }

      /* supplier dispatch state per variant, written nightly by the Luxottica sync */
      var availMap = {};
      var availTag = wrap.querySelector('[data-avail-map]');
      if (availTag) { try { availMap = JSON.parse(availTag.textContent) || {}; } catch (e) { availMap = {}; } }

      /* Mirrors the Liquid that paints the first frame. The copy talks about dispatch,
         never about our own shelves: what we know is what Luxottica ships and when. */
      function updateStructuredData(variant, row) {
        var tag = document.getElementById('bvo-product-jsonld');
        if (!tag) return;
        var data = JSON.parse(tag.textContent);
        var offer = data.offers;
        var blocked = !variant.available || row.s === 'not_carried';
        var backorder = !blocked && !row.h && row.s === 'backorder';
        var min = row.h ? 0 : backorder ? 16 : 4;
        var max = row.h ? 1 : backorder ? 16 : 6;
        if (!blocked) { var h = window.BVO_DATES.handling(min, max); min = h.min; max = h.max; }
        var url = new URL(offer.url, window.location.origin);
        url.searchParams.set('variant', variant.id);
        offer.url = url.toString();
        offer.price = variant.price / 100;
        offer.availability = 'https://schema.org/' + (blocked ? 'OutOfStock' : backorder ? 'BackOrder' : 'InStock');
        if (variant.sku) data.sku = variant.sku;
        else delete data.sku;
        if (blocked) {
          delete offer.shippingDetails;
        } else {
          offer.shippingDetails = {
            '@type': 'OfferShippingDetails',
            shippingRate: {'@type': 'MonetaryAmount', value: 0, currency: 'USD'},
            shippingDestination: {'@type': 'DefinedRegion', addressCountry: 'US'},
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {'@type': 'QuantitativeValue', minValue: min, maxValue: max, unitCode: 'DAY'},
              transitTime: {'@type': 'QuantitativeValue', minValue: 3, maxValue: 5, unitCode: 'DAY'}
            }
          };
        }
        if (backorder) offer.availabilityStarts = window.BVO_DATES.dispatchISO(16);
        else delete offer.availabilityStarts;
        tag.textContent = JSON.stringify(data);
      }

      function dispatchLine(variant) {
        var row = availMap[variant.id] || {};
        if (!variant.available || row.s === 'not_carried') {
          return { text: 'Currently unavailable', dot: 'dot--out', buy: 'Unavailable' };
        }
        if (row.h) {
          return { text: window.BVO_DATES.line('shelf'), dot: '', buy: 'Add to cart' };
        }
        if (row.s === 'backorder') {
          return { text: window.BVO_DATES.boText(), dot: 'dot--wait', buy: 'Add to cart' };
        }
        if (row.s === 'low_stock') {
          var q = parseInt(row.q, 10);
          return {
            text: window.BVO_DATES.line('supplier'),
            dot: 'dot--low',
            buy: 'Add to cart'
          };
        }
        return { text: window.BVO_DATES.line('supplier'), dot: '', buy: 'Add to cart' };
      }

      var colourIndex = parseInt(wrap.getAttribute('data-colour-index'), 10);
      if (isNaN(colourIndex)) colourIndex = -1;
      var moneyFormat = wrap.getAttribute('data-money-format') || '${{amount}}';

      var idInput = wrap.querySelector('input[name="id"]');
      var priceEl = wrap.querySelector('[data-price]');
      var compareEl = wrap.querySelector('[data-compare-price]');
      var buyBtn = wrap.querySelector('[data-add-button]');
      var skuWrap = wrap.querySelector('[data-sku-wrap]');
      var skuEl = wrap.querySelector('[data-sku]');
      var availDot = wrap.querySelector('[data-availability] .dot');
      var availText = wrap.querySelector('[data-availability-text]');
      var colourLabel = wrap.querySelector('[data-colour-label]');
      var frameColourEl = wrap.querySelector('[data-frame-colour]');
      var lensColourEl = wrap.querySelector('[data-lens-colour]');
      var lensRow = wrap.querySelector('[data-lens-row]');
      var mainImg = wrap.querySelector('[data-main-image]');
      var thumbsWrap = wrap.querySelector('[data-thumbs]');

      var startId = idInput ? parseInt(idInput.value, 10) : 0;
      var current = byId(startId) || product.variants[0];
      var chosen = (current.options || []).slice();

      function byId(id) {
        if (!id) return null;
        for (var i = 0; i < product.variants.length; i++) {
          if (product.variants[i].id === id) return product.variants[i];
        }
        return null;
      }

      function findVariant(opts) {
        for (var i = 0; i < product.variants.length; i++) {
          var v = product.variants[i];
          var hit = true;
          for (var j = 0; j < opts.length; j++) {
            if (v.options[j] !== opts[j]) { hit = false; break; }
          }
          if (hit) return v;
        }
        return null;
      }

      /* ---- money ---- */
      function withCommas(num, precision, thousands, decimal) {
        if (isNaN(num) || num === null) num = 0;
        var parts = (num / 100).toFixed(precision).split('.');
        var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
        return whole + (parts[1] ? decimal + parts[1] : '');
      }
      function money(cents) {
        var match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
        var token = match ? match[1] : 'amount';
        var value;
        switch (token) {
          case 'amount_no_decimals': value = withCommas(cents, 0, ',', '.'); break;
          case 'amount_with_comma_separator': value = withCommas(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = withCommas(cents, 0, '.', ','); break;
          case 'amount_with_space_separator': value = withCommas(cents, 2, ' ', ','); break;
          case 'amount_no_decimals_with_space_separator': value = withCommas(cents, 0, ' ', ','); break;
          case 'amount_with_apostrophe_separator': value = withCommas(cents, 2, "'", '.'); break;
          default: value = withCommas(cents, 2, ',', '.');
        }
        return moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
      }

      /* ---- gallery ---- */
      function setMain(item) {
        if (!mainImg || !item) return;
        mainImg.src = atWidth(item.u, 1100);
        mainImg.srcset = atWidth(item.u, 700) + ' 700w, ' + atWidth(item.u, 1100) + ' 1100w';
        mainImg.alt = item.a || '';
      }
      function setGallery(colour, preferSrc) {
        var list = colourMedia[colour];
        if (!list || !list.length) return;
        var idx = 0;
        var want = fileOf(preferSrc);
        if (want) {
          for (var i = 0; i < list.length; i++) {
            if (fileOf(list[i].u) === want) { idx = i; break; }
          }
        }
        setMain(list[idx]);
        if (!thumbsWrap) return;
        thumbsWrap.textContent = '';
        list.forEach(function (item, i) {
          var button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('data-thumb', '');
          button.setAttribute('data-src', atWidth(item.u, 1100));
          button.setAttribute('data-srcset', atWidth(item.u, 700) + ' 700w, ' + atWidth(item.u, 1100) + ' 1100w');
          button.setAttribute('data-alt', item.a || '');
          button.setAttribute('aria-label', 'View image ' + (i + 1));
          if (i === idx) button.className = 'is-active';
          var img = document.createElement('img');
          img.src = atWidth(item.u, 160);
          img.alt = '';
          img.loading = 'lazy';
          img.width = 76;
          img.height = 76;
          button.appendChild(img);
          thumbsWrap.appendChild(button);
        });
        thumbsWrap.hidden = list.length < 2;
      }

      /* ---- colour-dependent specs ----
         The colour option value is the authoritative text: frame colour before
         the separator, lens colour after. The separator is ' / ' — spaced —
         because bare slashes belong to the colour names ("Top Violet/opal
         Violet") and to the codes that disambiguate them ("Arista Gold
         (001/3E)"). Mirrors the split done in main-product.liquid. */
      function splitColour(value) {
        var text = String(value == null ? '' : value);
        var at = text.indexOf(' / ');
        if (at === -1) return { frame: text, lens: '' };
        return { frame: text.slice(0, at), lens: text.slice(at + 3) };
      }

      /* ---- sku ----
         Luxottica states a reference as MODEL COLOUR SIZE — "RB3025 001/C5 62"
         — where the colour code may be one block ("181", "9219R5") or split
         ("001/C5", "W44/87", "9123/3M"). The catalogue stores it dash-joined,
         so the display form is the first segment, the last segment, and
         whatever lies between them rejoined on a slash. Anything that is not
         at least model-colour-size prints as stored. Presentation only — the
         SKU on the variant is never touched. Mirrors snippets/bvo-sku.liquid,
         which does the same for first paint. */
      function formatSku(sku) {
        var raw = String(sku == null ? '' : sku).trim();
        var parts = raw.split('-');
        if (parts.length < 3) return raw;
        for (var i = 0; i < parts.length; i++) {
          if (!parts[i]) return raw;
        }
        return parts[0] + ' ' + parts.slice(1, -1).join('/') + ' ' + parts[parts.length - 1];
      }

      /* ---- controls ---- */
      function refreshControls() {
        wrap.querySelectorAll('[data-swatch]').forEach(function (b) {
          var on = colourIndex > -1 && b.getAttribute('data-value') === chosen[colourIndex];
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
        if (colourLabel && colourIndex > -1) colourLabel.textContent = chosen[colourIndex];

        if (colourIndex > -1) {
          var colourParts = splitColour(chosen[colourIndex]);
          if (frameColourEl) frameColourEl.textContent = colourParts.frame;
          if (lensColourEl) lensColourEl.textContent = colourParts.lens;
          if (lensRow) lensRow.hidden = !colourParts.lens;
        }

        wrap.querySelectorAll('[data-pill]').forEach(function (b) {
          var i = parseInt(b.getAttribute('data-option-index'), 10);
          var val = b.getAttribute('data-value');
          b.setAttribute('aria-checked', chosen[i] === val ? 'true' : 'false');
          var exists = false;
          var available = false;
          product.variants.forEach(function (v) {
            if (v.options[i] !== val) return;
            if (colourIndex > -1 && v.options[colourIndex] !== chosen[colourIndex]) return;
            exists = true;
            if (v.available) available = true;
          });
          b.disabled = !exists;
          b.classList.toggle('pill--out', !available);
        });
      }

      function refreshInfo() {
        if (idInput) idInput.value = current.id;
        if (priceEl) priceEl.textContent = money(current.price);
        if (compareEl) {
          if (current.compare_at_price && current.compare_at_price > current.price) {
            compareEl.textContent = money(current.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        if (skuWrap) {
          if (current.sku) {
            if (skuEl) skuEl.textContent = formatSku(current.sku);
            skuWrap.hidden = false;
          } else {
            skuWrap.hidden = true;
          }
        }
        var disp = dispatchLine(current);
        try { updateStructuredData(current, availMap[current.id] || {}); } catch (error) { console.warn("Product structured data could not be refreshed", error); }
        if (availText) availText.textContent = disp.text;
        if (availDot) {
          availDot.className = 'dot' + (disp.dot ? ' ' + disp.dot : '');
        }
        if (buyBtn) {
          buyBtn.disabled = !current.available;
          buyBtn.textContent = disp.buy;
        }
      }

      function apply(variant, pushUrl) {
        if (!variant) return;
        var previousColour = colourIndex > -1 ? chosen[colourIndex] : null;
        current = variant;
        chosen = (variant.options || []).slice();
        refreshControls();
        refreshInfo();
        if (colourIndex > -1) {
          var colour = chosen[colourIndex];
          if (colour !== previousColour) {
            setGallery(colour, variant.featured_image && variant.featured_image.src);
          }
        }
        if (pushUrl) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      function select(index, value) {
        if (isNaN(index) || chosen[index] === value) return;
        var next = chosen.slice();
        next[index] = value;
        var variant = findVariant(next);
        if (!variant) {
          /* combination doesn't exist (e.g. this colour isn't cut in that size) —
             fall back to an available variant of the value the shopper picked */
          var candidates = product.variants.filter(function (v) { return v.options[index] === value; });
          for (var i = 0; i < candidates.length; i++) {
            if (candidates[i].available) { variant = candidates[i]; break; }
          }
          if (!variant) variant = candidates[0];
        }
        apply(variant, true);
      }

      wrap.addEventListener('click', function (event) {
        if (!event.target.closest) return;
        var button = event.target.closest('[data-swatch], [data-pill]');
        if (!button || !wrap.contains(button) || button.disabled) return;
        event.preventDefault();
        select(parseInt(button.getAttribute('data-option-index'), 10), button.getAttribute('data-value'));
      });

      /* deep link: the server already resolves ?variant=, this only catches a
         cached document being restored with a different parameter */
      refreshInfo();
      document.addEventListener('bvo:dates-updated', refreshInfo);

      var deepLink = parseInt(new URL(window.location.href).searchParams.get('variant'), 10);
      if (deepLink && deepLink !== current.id) {
        var linked = byId(deepLink);
        if (linked) apply(linked, false);
      }
    });

    /* ---------- collection sorting ---------- */
    var sortSelect = document.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    }
  });
})();

/* BVO business-day date engine — added 17 Sep 2026. Google US non-business days; 2pm ET cutoff. */
window.BVO_DATES=(function(){
  var H={};['2026-01-01','2026-01-19','2026-02-16','2026-05-25','2026-06-19','2026-07-03','2026-09-07','2026-11-26','2026-12-25','2027-01-01','2027-01-18','2027-02-15','2027-05-31','2027-06-18','2027-07-05','2027-09-06','2027-11-25','2027-12-24','2027-12-31','2028-01-17','2028-02-21','2028-05-29','2028-06-19','2028-07-04','2028-09-04','2028-11-23','2028-12-25','2029-01-01','2029-01-15','2029-02-19','2029-05-28','2029-06-19','2029-07-04','2029-09-03','2029-11-22','2029-12-25','2030-01-01','2030-01-21','2030-02-18','2030-05-27','2030-06-19','2030-07-04','2030-09-02','2030-11-28','2030-12-25'].forEach(function(d){H[d]=1;});
  var cutoff=14;
  var close='2026-09-25',reopen='2026-10-05';
  var noticeStart=Date.parse('2026-09-23T00:00:00-04:00'),noticeEnd=Date.parse('2026-10-05T00:00:00-04:00');
  var M=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function iso(d){return d.toISOString().slice(0,10);}
  function biz(d){var w=d.getUTCDay();return w>0&&w<6&&!H[iso(d)];}
  function processing(d){var s=iso(d);return biz(d)&&!(s>=close&&s<reopen);}
  function next(d){return new Date(d.getTime()+86400000);}
  function add(d,n,test){var x=new Date(d.getTime()),c=0;if(!Number.isInteger(n)||n<0)throw Error('Invalid day count');while(c<n){x=next(x);if(test(x))c++;}return x;}
  function et(){var o={};new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',hour12:false}).formatToParts(new Date()).forEach(function(x){o[x.type]=x.value;});return{y:+o.year,m:+o.month,d:+o.day,h:(+o.hour)%24};}
  function googleBase(c){var p=et(),d=new Date(Date.UTC(p.y,p.m-1,p.d));if(p.h>=c||!biz(d))d=add(d,1,biz);return d;}
  function base(){var d=googleBase(cutoff);while(!processing(d))d=next(d);return d;}
  function dispatchDate(n){return add(base(),n,processing);}
  function f(d){return M[d.getUTCMonth()]+' '+d.getUTCDate();}
  function range(a,b){if(iso(a)===iso(b))return f(a);return a.getUTCMonth()===b.getUTCMonth()?f(a)+'–'+b.getUTCDate():f(a)+' – '+f(b);}
  function offsetISO(d){var sample=new Date(d.getTime()+43200000);var z=new Intl.DateTimeFormat('en-US',{timeZone:'America/New_York',timeZoneName:'longOffset'}).formatToParts(sample).filter(function(p){return p.type==='timeZoneName';})[0].value.replace('GMT','');if(!/^[+-]\d{2}:\d{2}$/.test(z))throw Error('New York offset unavailable');return iso(d)+'T00:00:00'+z;}
  function count(from,to){var n=0,x=from;if(to<from)throw Error('Dispatch precedes order base');while(x<to){x=next(x);if(biz(x))n++;}return n;}
  function affected(a,b){return iso(dispatchDate(a))!==iso(add(googleBase(cutoff),a,biz))||iso(dispatchDate(b))!==iso(add(googleBase(cutoff),b,biz));}
  function line(g){if(g==='backorder')return api.boText();var a=g==='shelf'?0:4,b=g==='shelf'?1:6;if(!affected(a,b))return g==='shelf'?'Ships same or next business day':'Ships in 4–6 business days';return 'Estimated dispatch '+range(dispatchDate(a),dispatchDate(b))+'; estimated delivery '+range(add(dispatchDate(a),3,biz),add(dispatchDate(b),5,biz));}
  var api={
    dispatch:function(n){return f(dispatchDate(n));},
    dispatchISO:function(n){return offsetISO(dispatchDate(n));},
    delivery:function(a,b){return range(add(dispatchDate(16),a-16,biz),add(dispatchDate(16),b-16,biz));},
    boText:function(){return 'On backorder — estimated dispatch '+this.dispatch(16)+', estimated delivery '+this.delivery(19,21);},
    handling:function(a,b,referenceCutoff){var d=googleBase(referenceCutoff===undefined?14:referenceCutoff);return{min:count(d,dispatchDate(a)),max:count(d,dispatchDate(b))};},
    noticeActive:function(){return Date.now()>=noticeStart&&Date.now()<noticeEnd;},
    closureAffects:affected,
    line:line
  };
  return api;
})();
(function(){
  function paint(){
    try{
      document.querySelectorAll('[data-bvo-dispatch]').forEach(function(e){e.textContent=window.BVO_DATES.line(e.getAttribute('data-bvo-dispatch'));});
      document.querySelectorAll('[data-bvo-closure-notice]').forEach(function(e){e.hidden=!window.BVO_DATES.noticeActive();});
      document.dispatchEvent(new CustomEvent('bvo:dates-updated'));
    }catch(err){}
  }
  if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',paint);}else{paint();}
  window.setInterval(paint,60000);
  document.addEventListener('visibilitychange',function(){if(!document.hidden)paint();});
})();
